import { Alert, Box, Grid2, Stack } from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";

import { CardLayout } from "../v3";
import { useCrmOperationalDashboard } from "../../hooks/useCrmOperationalDashboard";
import { CRM_PRO_ROUTES } from "../../pages/crm-pro/constants";
import { AssigneeWorkloadWidget } from "./AssigneeWorkloadWidget.component";
import { CrmActivityTimeline } from "./CrmActivityTimeline.component";
import { CRM_DASHBOARD_SECTION_ORDER, type CrmDashboardSectionId } from "./crmDashboard.config";
import { CrmDashboardHeader } from "./CrmDashboardHeader.component";
import { CrmDashboardKpiRow } from "./CrmDashboardKpiRow.component";
import { DashboardTaskPanel } from "./DashboardTaskPanel.component";
import { TaskStatusDistributionChart } from "./TaskStatusDistributionChart.component";
import { ProjectSummaryChart } from "../dashboard";
import moment from "moment";
import { WorkflowMetricsChart } from "./WorkflowMetricsChart.component";

export const CrmDashboard = () => {
  const navigate = useNavigate();
  const { loading, error, refresh, model } = useCrmOperationalDashboard();

  const handleGoLeads = () => {
    navigate(CRM_PRO_ROUTES.leads);
  };

  const renderSection = (sectionId: CrmDashboardSectionId) => {
    switch (sectionId) {
      case "kpis":
        return (
          <Grid2>
            <CardLayout sx={{ mb: 0 }}>
              <CrmDashboardKpiRow model={model} loading={loading} onNavigateLeads={handleGoLeads} />
            </CardLayout>
          </Grid2>
        );
      case "workflowMetrics":
        return (
          <Grid2 container>
            <Grid2 size={{ xs: 12, md: 12 }}>
              <CardLayout sx={{ height: "100%", mb: 0 }}>
                <WorkflowMetricsChart data={model?.workflowMetricsChart ?? []} loading={loading} />
              </CardLayout>
            </Grid2>
          </Grid2>
        );
      case "analytics":
        return (
          <Grid2 container style={{ overflowY: "hidden" }}>
            <Grid2 size={{ xs: 12, lg: 6 }}>
              <CardLayout sx={{ height: "100%", mb: 0 }}>
                <TaskStatusDistributionChart data={model?.taskStatusChart ?? []} loading={loading} />
              </CardLayout>
            </Grid2>
            <Grid2 size={{ xs: 12, lg: 6 }}>
              <CardLayout sx={{ height: "100%", mb: 0 }}>
                <AssigneeWorkloadWidget rows={model?.workload ?? []} loading={loading} />
              </CardLayout>
            </Grid2>
          </Grid2>
        );
      case "taskPanels":
        return (
          <Grid2 key={sectionId} container>
            <Grid2 size={{ xs: 12, md: 4 }}>
              <CardLayout sx={{ height: "100%", mb: 0 }}>
                <DashboardTaskPanel
                  title="Tasks due today"
                  subtitle="Incomplete tasks with a due date of today."
                  tasks={model?.tasksDueToday ?? []}
                  loading={loading}
                />
              </CardLayout>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 4 }}>
              <CardLayout sx={{ height: "100%", mb: 0 }}>
                <DashboardTaskPanel
                  title="Overdue tasks"
                  subtitle="Incomplete tasks past their due date."
                  tasks={model?.overdueTasks ?? []}
                  loading={loading}
                  urgentTone
                  emptyLabel="No overdue tasks"
                />
              </CardLayout>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 4 }}>
              <CardLayout sx={{ height: "100%", mb: 0 }}>
                <Stack spacing={2}>
                  <Box>
                    <Box component="span" sx={{ fontWeight: 600, fontSize: "0.875rem", display: "block", mb: 0.5 }}>
                      Recent activity
                    </Box>
                    <Box component="span" sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                      Recent lead and task activity across your pipeline.
                    </Box>
                  </Box>
                  <CrmActivityTimeline groups={model?.timeline ?? []} loading={loading} />
                </Stack>
              </CardLayout>
            </Grid2>
          </Grid2>
        );
      // case "timeline":
      //   return (
      //     <Grid2 key={sectionId} container>
      //       <Grid2 size={{ xs: 12, md: 6 }}>
      //         <CardLayout sx={{ mb: 0 }}>
      //           <Stack spacing={2}>
      //             <Box>
      //               <Box component="span" sx={{ fontWeight: 600, fontSize: "0.875rem", display: "block", mb: 0.5 }}>
      //                 Recent activity
      //               </Box>
      //               <Box component="span" sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
      //                 Lead and task events from your scoped pipeline (IndexedDB today; API later).
      //               </Box>
      //             </Box>
      //             <CrmActivityTimeline groups={model?.timeline ?? []} loading={loading} />
      //           </Stack>
      //         </CardLayout>
      //       </Grid2>
      //       <Grid2 size={{ xs: 12, md: 6 }}></Grid2>
      //     </Grid2>
      //   );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ mt: -7, pb: "22px", zIndex: 2, position: "relative", width: "100%" }}>
      <Stack spacing={2}>
        {error ? <Alert severity="error">{error.message}</Alert> : null}
        {CRM_DASHBOARD_SECTION_ORDER.map((id) => (
          <React.Fragment key={id}>{renderSection(id)}</React.Fragment>
        ))}
      </Stack>
    </Box>
  );
};
